import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type GradeCategory = {
  id: string;
  name: string;
  sourceType: string;
  weightPct: number;
};

type GradebookRow = {
  userId: string;
  studentName: string | null;
  studentEmail: string;
  categoryScores: Record<string, number | null>;
  finalScorePct: number | null;
};

type GradebookPayload = {
  categories: GradeCategory[];
  rows: GradebookRow[];
};

type CourseGradebookProps = {
  tenantSlug: string;
  courseId: string;
  courseTitle: string;
};

function formatScore(score: number | null | undefined): string {
  if (score == null) return "—";
  return `${score}%`;
}

export function CourseGradebook({ tenantSlug, courseId, courseTitle }: CourseGradebookProps) {
  const t = useTranslations("instructor");
  // C4 (CRS-16): server errors are shown by code, never as the server's English.
  const tRoot = useTranslations();
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [gradebook, setGradebook] = useState<GradebookPayload | null>(null);
  const [weightDrafts, setWeightDrafts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [savingWeights, setSavingWeights] = useState(false);
  const [weightsSaved, setWeightsSaved] = useState(false);

  useEffect(() => {
    let active = true;
    // The `active` flag is checked after the parse, so it cannot help when the
    // parse itself rejects on a truncated response. Aborting can.
    const controller = new AbortController();

    void (async () => {
      try {
        const res = await apiFetch(`/v1/instructor/courses/${courseId}/gradebook`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (!active) return;

        if (!res.ok) {
          setError(errorText(json, "gradebookLoadFailed"));
          setLoading(false);
          return;
        }

        const payload = json.data as GradebookPayload;
        setGradebook(payload);
        setWeightDrafts(
          Object.fromEntries(
            payload.categories.map((category) => [category.id, category.weightPct]),
          ),
        );
        setLoading(false);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setError(t("gradebookLoadFailed"));
        setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId, t]);

  const weightTotal = gradebook
    ? gradebook.categories.reduce((sum, category) => sum + (weightDrafts[category.id] ?? 0), 0)
    : 0;
  const weightsValid = weightTotal === 100;

  async function reloadGradebook() {
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/gradebook`);
    const json = await res.json();
    if (!res.ok) {
      setError(errorText(json, "gradebookLoadFailed"));
      return;
    }
    const payload = json.data as GradebookPayload;
    setGradebook(payload);
    setWeightDrafts(
      Object.fromEntries(payload.categories.map((category) => [category.id, category.weightPct])),
    );
  }

  async function handleSaveWeights() {
    if (!gradebook || !weightsValid || savingWeights) return;

    setSavingWeights(true);
    setError(null);
    setWeightsSaved(false);

    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/gradebook`, {
        method: "PATCH",
        body: JSON.stringify({
          categories: gradebook.categories.map((category) => ({
            id: category.id,
            weightPct: weightDrafts[category.id] ?? category.weightPct,
          })),
        }),
      });
      const json = await res.json();

      if (!res.ok) {
        setError(errorText(json, "gradebookWeightsSaveFailed"));
        return;
      }

      await reloadGradebook();
      setWeightsSaved(true);
    } finally {
      setSavingWeights(false);
    }
  }

  async function handleExportCsv() {
    setExporting(true);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/gradebook?format=csv`, {
        method: "POST",
      });
      if (!res.ok) {
        setError(t("gradebookExportFailed"));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `gradebook-${courseId}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("gradebookLoading")}</p>;
  }

  if (error && !gradebook) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!gradebook) {
    return <p className="text-sm text-[var(--text-muted)]">{t("gradebookUnavailable")}</p>;
  }

  return (
    <section data-testid="course-gradebook">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("gradebookTitle")}</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{courseTitle}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={exporting}
            className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="gradebook-export-csv"
          >
            {exporting ? t("gradebookExporting") : t("gradebookExportCsv")}
          </button>
          <Link
            to={`/t/${tenantSlug}/instructor/courses/${courseId}`}
            className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm"
          >
            {t("gradebookBackToBuilder")}
          </Link>
        </div>
      </div>

      {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}

      <div
        className="mt-4 rounded border border-[var(--border)] p-4"
        data-testid="gradebook-weights"
      >
        <p className="text-sm text-[var(--text-muted)]">{t("gradebookWeightsHint")}</p>
        <ul className="mt-3 space-y-2">
          {gradebook.categories.map((category) => (
            <li key={category.id} className="flex flex-wrap items-center gap-2 text-sm">
              <label htmlFor={`grade-weight-${category.id}`} className="min-w-28 font-medium">
                {category.name}
              </label>
              <input
                id={`grade-weight-${category.id}`}
                type="number"
                min={0}
                max={100}
                value={weightDrafts[category.id] ?? category.weightPct}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  setWeightDrafts((current) => ({
                    ...current,
                    [category.id]: Number.isNaN(next) ? 0 : next,
                  }));
                  setWeightsSaved(false);
                }}
                className="w-20 rounded border border-[var(--border-strong)] px-2 py-1"
                data-testid={`gradebook-weight-${category.sourceType}`}
              />
              <span>%</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSaveWeights()}
            disabled={savingWeights || !weightsValid}
            className="rounded bg-[var(--ink-12)] px-3 py-1.5 text-sm text-[var(--ink-1)] disabled:opacity-50"
            data-testid="gradebook-save-weights"
          >
            {savingWeights ? t("gradebookWeightsSaving") : t("gradebookWeightsSave")}
          </button>
          <p
            className={`text-sm ${weightsValid ? "text-[var(--text-muted)]" : "text-red-600"}`}
            data-testid="gradebook-weight-total"
          >
            {t("gradebookWeightsTotal", { total: weightTotal })}
          </p>
          {weightsSaved ? (
            <p className="text-sm text-green-700" data-testid="gradebook-weights-saved">
              {t("gradebookWeightsSaved")}
            </p>
          ) : null}
        </div>
      </div>

      {gradebook.rows.length === 0 ? (
        <p className="mt-6 text-sm text-[var(--text-muted)]">{t("gradebookNoStudents")}</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="px-3 py-2 font-medium">{t("gradebookStudent")}</th>
                <th className="px-3 py-2 font-medium">{t("gradebookEmail")}</th>
                {gradebook.categories.map((category) => (
                  <th key={category.id} className="px-3 py-2 font-medium">
                    {category.name}
                  </th>
                ))}
                <th className="px-3 py-2 font-medium">{t("gradebookFinal")}</th>
              </tr>
            </thead>
            <tbody>
              {gradebook.rows.map((row) => (
                <tr
                  key={row.userId}
                  className="border-b border-[var(--border)]"
                  data-testid={`gradebook-row-${row.userId}`}
                >
                  <td className="px-3 py-2">{row.studentName ?? "—"}</td>
                  <td className="px-3 py-2">{row.studentEmail}</td>
                  {gradebook.categories.map((category) => (
                    <td
                      key={category.id}
                      className="px-3 py-2"
                      data-testid={`gradebook-${category.sourceType}-${row.userId}`}
                    >
                      {formatScore(row.categoryScores[category.sourceType])}
                    </td>
                  ))}
                  <td
                    className="px-3 py-2 font-medium"
                    data-testid={`gradebook-final-${row.userId}`}
                  >
                    {formatScore(row.finalScorePct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
